import { Toggle as BaseUIToggle } from "@base-ui/react/toggle";
import { ToggleGroup as BaseUIToggleGroup } from "@base-ui/react/toggle-group";
import classNames from "classnames";

import styles from "./ToggleGroup.module.css";

/** Control density: drives item height, inline padding, and font size. */
export type ToggleSize = "sm" | "md" | "lg";

export type ToggleGroupProps<Value extends string = string> =
  BaseUIToggleGroup.Props<Value> & {
    /**
     * Control density — sets the item height, inline padding, and font size for
     * the whole group.
     * @default "md"
     */
    size?: ToggleSize;
  };

const sizeClass: Record<ToggleSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

/**
 * A row of toggle buttons
 *
 * @example
 * <ToggleGroup value={value} onValueChange={setValue} aria-label="Time range">
 *   <Toggle value="30d">Last 30 days</Toggle>
 * </ToggleGroup>
 */
export function ToggleGroup<Value extends string = string>({
  size = "md",
  className,
  ...props
}: ToggleGroupProps<Value>) {
  return (
    <BaseUIToggleGroup
      className={(state) =>
        classNames(
          styles.group,
          sizeClass[size],
          typeof className === "function" ? className(state) : className,
        )
      }
      {...props}
    />
  );
}

export type ToggleProps<Value extends string = string> =
  BaseUIToggle.Props<Value>;

/**
 * One button in a {@link ToggleGroup}.
 */
export function Toggle<Value extends string = string>({
  className,
  ...props
}: ToggleProps<Value>) {
  return (
    <BaseUIToggle
      className={(state) =>
        classNames(
          styles.item,
          typeof className === "function" ? className(state) : className,
        )
      }
      {...props}
    />
  );
}
